import { useForm, Head, usePage } from '@inertiajs/react'
import { useTranslation } from 'react-i18next'
import { FormEvent } from 'react'
import LanguageSwitcher from '@components/layout/LanguageSwitcher'
import ThemeSwitcher from '@components/layout/ThemeSwitcher'

interface FlashData {
  key: string
  [key: string]: any
}

interface PageProps {
  flash: {
    error?: FlashData | string
    success?: FlashData | string
    warning?: FlashData | string
  }
  [key: string]: any
}

export default function SignIn() {
  const { data, setData, post, processing, errors } = useForm({
    email_address: '',
    password: '',
  })
  const { t } = useTranslation()
  const { flash } = usePage<PageProps>().props

  const getFlashMessage = (message: FlashData | string): string => {
    if (typeof message === 'string') {
      return message
    }
    const { key, ...params } = message
    return String(t(key, params))
  }

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault()
    post('/session')
  }

  return (
    <>
      <Head title={t('titles.signIn')} />
      <div className="min-h-screen flex items-center justify-center bg-base-200 p-4">
        <div className="absolute top-4 right-4 flex items-center gap-2">
          <ThemeSwitcher />
          <LanguageSwitcher />
        </div>

      <div className="w-full max-w-md space-y-4">

        {/* Demo Notice */}
        <div className="card bg-info/40 text-info-content shadow-xl">
          <div className="card-body p-4 sm:p-6 text-center">
            <h3 className="font-bold text-lg flex items-center justify-center gap-2">
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" className="stroke-current flex-shrink-0 w-6 h-6">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
              </svg>
              {t('auth.demo.title')}
            </h3>
            <p className="text-sm opacity-90">{t('auth.demo.reset')}</p>
            <p className="text-sm opacity-90">{t('auth.demo.experiment')}</p>
            <div className="card-actions mt-2 justify-center">
              <button
                type="button"
                className="btn btn-sm btn-primary btn-soft w-full gap-2"
                onClick={() => {
                  setData({
                    email_address: 'alice.johnson@yopmail.com',
                    password: 'password123',
                  })
                }}
              >
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.501 20.118a7.5 7.5 0 0114.998 0A17.933 17.933 0 0112 21.75c-2.676 0-5.216-.584-7.499-1.632z" />
                </svg>
                {t('auth.fillTestCredentials')}
              </button>
            </div>
          </div>
        </div>

        {/* Error Alert */}
        {flash?.error && (
          <div className="alert alert-error shadow-xl" data-testid="error-alert">
            <svg xmlns="http://www.w3.org/2000/svg" className="stroke-current shrink-0 h-6 w-6" fill="none" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span>{getFlashMessage(flash.error)}</span>
          </div>
        )}
        
        {/* Sign In Card */}
        <div className="card bg-base-100 shadow-xl">
        <div className="card-body">
          <h2 className="card-title text-center justify-center text-2xl mb-4">
            {t('auth.signIn')}
          </h2>

          <form onSubmit={handleSubmit}>
            <div className="form-control">
              <label className="label">
                <span className="label-text">{t('auth.email')}</span>
              </label>
              <input
                type="email"
                placeholder="email@example.com"
                className={`input input-bordered w-full ${errors.email_address ? 'input-error' : ''}`}
                value={data.email_address}
                onChange={(e) => setData('email_address', e.target.value)}
                required
                data-testid="email-input"
              />
              {errors.email_address && (
                <label className="label">
                  <span className="label-text-alt text-error">{errors.email_address}</span>
                </label>
              )}
            </div>

            <div className="form-control mt-4">
              <label className="label">
                <span className="label-text">{t('auth.password')}</span>
              </label>
              <input
                type="password"
                placeholder={t('auth.password')}
                className={`input input-bordered w-full ${errors.password ? 'input-error' : ''}`}
                value={data.password}
                onChange={(e) => setData('password', e.target.value)}
                required
                data-testid="password-input"
              />
              {errors.password && (
                <label className="label">
                  <span className="label-text-alt text-error">{errors.password}</span>
                </label>
              )}
            </div>

            <div className="form-control mt-6">
              <button
                type="submit"
                className="btn btn-primary w-full"
                disabled={processing}
                data-testid="sign-in-button"
              >
                {processing ? t('auth.signingIn') : t('auth.signIn')}
              </button>
            </div>
          </form>
        </div>
        </div>
      </div>
    </div>
    </>
  )
}
